"use client";
import React, { useState, useCallback } from "react";
import styles from "./InputDesign.module.css";
import PagesSidebar from "./PagesSidebar";
import PageContent from "./PageContent";
import PreviewArea from "./PreviewArea";
import StyleEditor from "./StyleEditor";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { AppContext } from "../../AppContext";
import { useContext } from "react";

const MenuEditor = () => {
  const fbResponse = useContext(AppContext).get("fbResponse");
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedPage, setDraggedPage] = useState(null);
  const [draggedOverPage, setDraggedOverPage] = useState(null);
  const [showStyle, setShowStyle] = React.useState(false);
  const [showJsonDialog, setShowJsonDialog] = React.useState(false);
  const [styleSettings, setStyleSettings] = React.useState({
    backgroundColor: "#ffffff",
    closeColor: "#2ec5b3",
    detailBackgroundColor: "#ffffff",
    detailCloseColor: "#2ec5b3",
    detailDescColor: "#000000",
    detailPriceColor: "#000000",
    detailTextColor: "#000000",
    priceColor: "#000000",
    textColor: "#000000",
    titleColor: "#000000",
    descColor: "#707070",
    titleFontSize: "24",
    textFont: {
      name: "Roboto",
      location: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap",
    },
    titleFont: {
      location: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap",
      name: "Roboto",
    },
    categoryFontStyleLikeTitle: false,
  });

  const addPage = useCallback(() => {
    const newPages = [
      ...pages,
      { pageType: { fr: "", en: "" }, pageEntries: [] },
    ];
    setPages(newPages);
    setSelectedPage(newPages.length - 1);
    setShowStyle(false);
  }, [pages]);

  const deletePage = useCallback(
    (index) => {
      const newPages = [...pages];
      newPages.splice(index, 1);
      setPages(newPages);
      setSelectedPage(null);
    },
    [pages]
  );

  const addPageEntry = useCallback(() => {
    if (selectedPage !== null) {
      const newPages = [...pages];
      newPages[selectedPage].pageEntries.push({
        entryType: "dishList",
        entries: [],
      });
      setPages(newPages);
    }
  }, [selectedPage, pages]);

  const deletePageEntry = useCallback(
    (index) => {
      if (selectedPage !== null) {
        const newPages = [...pages];
        newPages[selectedPage].pageEntries.splice(index, 1);
        setPages(newPages);
      }
    },
    [selectedPage, pages]
  );

  const addEntry = useCallback(
    (pageEntryIndex) => {
      if (selectedPage !== null) {
        const newPages = [...pages];
        newPages[selectedPage].pageEntries[pageEntryIndex].entries.push({
          entryId: Date.now(),
          entryTitle: { fr: "", en: "" },
          entrySubtitle: { fr: "", en: "" },
          entryPrice: { fr: [""], en: [""] },
        });
        setPages(newPages);
      }
    },
    [selectedPage, pages]
  );

  const deleteEntry = useCallback(
    (pageEntryIndex, entryIndex) => {
      if (selectedPage !== null) {
        const newPages = [...pages];
        newPages[selectedPage].pageEntries[pageEntryIndex].entries.splice(
          entryIndex,
          1
        );
        setPages(newPages);
      }
    },
    [selectedPage, pages]
  );

  const translatePage = useCallback(async () => {
    if (selectedPage !== null) {
      const newPages = [...pages];
      const page = newPages[selectedPage];

      // Translate page type
      if (page.pageType.fr && (!page.pageType.en || page.pageType.en.trim() === "")) {
        page.pageType.en = await translateText(page.pageType.fr);
      }

      // Translate page entries
      for (const pageEntry of page.pageEntries) {
        // Translate text
        if (pageEntry.text && pageEntry.text.fr && (!pageEntry.text.en || pageEntry.text.en.trim() === "")) {
          pageEntry.text.en = await translateText(pageEntry.text.fr);
        }

        // Translate entries title
        if (
          pageEntry.entriesTitle &&
          pageEntry.entriesTitle.fr &&
          (!pageEntry.entriesTitle.en || pageEntry.entriesTitle.en.trim() === "")
        ) {
          pageEntry.entriesTitle.en = await translateText(pageEntry.entriesTitle.fr);
        }

        // Translate entries
        if (pageEntry.entries) {
          for (const entry of pageEntry.entries) {
            // Translate entry title
            if (
              entry.entryTitle &&
              entry.entryTitle.fr &&
              (!entry.entryTitle.en || entry.entryTitle.en.trim() === "")
            ) {
              entry.entryTitle.en = await translateText(entry.entryTitle.fr);
            }

            // Translate entry subtitle
            if (
              entry.entrySubtitle &&
              entry.entrySubtitle.fr &&
              (!entry.entrySubtitle.en || entry.entrySubtitle.en.trim() === "")
            ) {
              entry.entrySubtitle.en = await translateText(entry.entrySubtitle.fr);
            }

            // Translate entry price
            if (
              entry.entryPrice &&
              entry.entryPrice.fr &&
              (!entry.entryPrice.en || entry.entryPrice.en.length === 0)
            ) {
              entry.entryPrice.en = [];
              for (const price of entry.entryPrice.fr) {
                if (price.match(/[a-zA-Z]+/)) {
                  entry.entryPrice.en.push(await translateText(price));
                } else {
                  entry.entryPrice.en.push(price);
                }
              }
            }
          }
        }
      }

      setPages(newPages);
    }
  }, [selectedPage, pages]);

  const translateText = async (text) => {
    let textTranslated = null;
    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: "Tu es un traducteur professionnel. Traduis le texte suivant du français vers l'anglais (il s'agit de page(s) d'un menu de restaurant). Retourne uniquement la traduction, sans commentaires ni explications. Si plusieurs textes sont fournis, retourne-les dans le même ordre, séparés par des virgules."
            },
            {
              role: "user",
              content: text
            }
          ],
          temperature: 0.3
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Erreur API ChatGPT:", errorData);
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      textTranslated = data.choices[0].message.content.trim();
      console.log("Translation result:", textTranslated);
    } catch (error) {
      console.error("Error translating text:", error);
      return text; // Return original text if translation fails
    }
    return textTranslated;
  };

  const handleCloseDialog = () => {
    setShowJsonDialog(false);
  };

  const handleDragStart = useCallback((index) => {
    setDraggedPage(index);
  }, []);

  const handleDragOver = useCallback((event, index) => {
    event.preventDefault();
    setDraggedOverPage(index);
  }, []);

  const handleDrop = useCallback(
    (event, index) => {
      event.preventDefault();

      if (draggedPage === null || draggedPage === index) return;

      const newPages = [...pages];
      const pageToMove = newPages[draggedPage];

      // Remove the page from its original position
      newPages.splice(draggedPage, 1);

      // Insert the page at the new position
      newPages.splice(index, 0, pageToMove);

      setPages(newPages);

      // Update selected page if needed
      if (selectedPage === draggedPage) {
        setSelectedPage(index);
      } else if (selectedPage === index) {
        // If dropping on the selected page, adjust the selection
        if (draggedPage < index) {
          setSelectedPage(selectedPage - 1);
        } else {
          setSelectedPage(selectedPage + 1);
        }
      }

      setDraggedPage(null);
      setDraggedOverPage(null);
    },
    [draggedPage, pages, selectedPage]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedPage(null);
    setDraggedOverPage(null);
  }, []);

  const toggleStyleEditor = () => {
    if (!showStyle) {
      setSelectedPage(null);
    }
    setShowStyle(!showStyle);
  };

  const handlePageSelect = useCallback((index) => {
    setSelectedPage(index);
    setShowStyle(false);
  }, []);

if (fbResponse != null && (fbResponse.isAnonymous == null || ! fbResponse.isAnonymous) && (fbResponse.uid === "3L90kLcGGyW6XilZmjINqFOUDWH3" || fbResponse.uid === "WgpXG6xvvYUdpit8zuK1PDDmXNM2") ){

    return (
      <main className={styles.flexFlexColFont}>
        <div className={styles.mainContent}>
          <PagesSidebar
            pages={pages}
            selectedPage={selectedPage}
            setSelectedPage={handlePageSelect}
            addPage={addPage}
            deletePage={deletePage}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            handleDragEnd={handleDragEnd}
            draggedOverPage={draggedOverPage}
            showStyle={showStyle}
            toggleStyleEditor={toggleStyleEditor}
            setPages={setPages}
            setStyleSettings={setStyleSettings}
            styleSettings={styleSettings}
          />

          {selectedPage !== null && (
            <PageContent
              selectedPage={selectedPage}
              pages={pages}
              updatePages={setPages}
              addPageEntry={addPageEntry}
              deletePageEntry={deletePageEntry}
              addEntry={addEntry}
              deleteEntry={deleteEntry}
              translatePage={translatePage}              
              isSaving={isSaving}
              setSelectedPage={setSelectedPage}
              setPages={setPages}
            />
          )}

          {showStyle && (
            <StyleEditor
              styleSettings={styleSettings}
              setStyleSettings={setStyleSettings}
              toggleStyleEditor={toggleStyleEditor}
            />
          )}

          <PreviewArea pages={pages} styleSettings={styleSettings} />

          {/* Dialog de prévisualisation JSON existant */}
          <Dialog open={showJsonDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle>Contenu de la page</DialogTitle>
            <DialogContent>
              <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                {JSON.stringify(selectedPage !== null ? pages[selectedPage] : {}, null, 2)}
              </pre>
            </DialogContent>
            <DialogActions>
              <button onClick={handleCloseDialog} className={styles.primaryButton}>
                Fermer
              </button>
            </DialogActions>
          </Dialog>
        </div>
      </main>
    );
  }
  else {
    return null;
  }
};

export default MenuEditor;

/*

Convertit le fichier attaché (c'est un menu de restaurant) en un format JSON structuré (voir exemple ci-dessous).

Consignes additionnelles : 
 - ne met pas en première page une page avec juste des notes, si le fichier menu contient une première page avec un long texte descriptif, ne le reprend pas.
 - pageType représentent les catégories de plat ou boisson des pages du menu, n'hésites pas à en ajouter si nécessaire (garde les noms du fichier menu).
 - S'il y a beaucoup de boisson, regroupe-les sur une même page. S'il y a différentes catégories, ajoute alors un sous-titre de catégorie entriesTitle.
 - ne met en entrySubtitle que les descriptions de plat ou boisson, si le texte du plat ou de la boisson est une énumération de ses ingrédients, mets-les dans entryTitle (ne découpe pas dans ce cas en entryTitle et entrySubtitle). Pour savoir découper en entryTitle et entrySubtitle, tu peux aussi regarder si le début du texte est en majuscule et la suite en minuscule (si c'est le cas, met le début en entryTitle et le reste en entrySubtitle). Une autre indication pour découper en entryTitle et entrySubtitle est si le texte commence par une couleur puis continue par une ou plusieurs autres, dans ce cas met le texte de la première couleur en entryTitle et le reste en entrySubtitle.
 - pour les vins, si tous les vins d'une même catégorie sont proposés dans le même litrage, ajoute le litrage dans le titre de la catégorie
 - si tu as des informations à mettre en page de fin dans une note tu peux utiliser des \n pour formatter le texte
 - remplis styleSettings avec les polices et couleurs du fichier menu
 * titleFont correspond à la police de titre des catégoris de plat ou boisson dans le fichier menu et textFont correspond à la police de texte des plats ou boissons. N'ajoute pas d'autres types de police.
 * backgroundColor correspond à la couleur de fond du fichier menu
 * dans location de textFont et titleFont, mets l'URL Google Font de la police si elle est disponible, sinon mets une chaîne vide.
 - si le fichier contient les traductions en anglais ajoute un champ "en" avec la traduction du fichier. N'ajoute pas de champ "en" sinon.
 - donne juste le JSON brut en réponse 


Exemple de structure JSON d'un menu complet :

{
  "pages": [
    {
        "pageType": {"fr": "Menus"},
        "pageEntries": [  
            {
                "entryType": "startingNote",
                "text": {
                    "fr": "LUNDI AU VENDREDI MIDI UNIQUEMENT (HORS JOURS FÉRIÉS)",
                },
                "centering": "center",                
            },                 
            {
                "entryType": "formula",
                "entriesTitle": {
                    "fr": "",
                },
                "entries": [
                    {
                        "entryId": 0,
                        "entryTitle": {
                            "fr": "",                            
                        },                                
                    },
                ],
            },
            {     
                "entryType": "formula",
                "entriesTitle": {
                    "fr": "ENTRÉE* + PLAT DU JOUR OU PLAT DU JOUR + DESSERT*",
                },                  
                "formulaPrice" : "19.90 €",                        
            },
            {
                "entryType": "formula",
                "entriesTitle": {
                    "fr": "",
                },
                "entries": [
                    {
                        "entryId": 0,
                        "entryTitle": {
                            "fr": "",                            
                        },                                
                    },
                ],
            },
            {     
                "entryType": "formula",
                "entriesTitle": {
                    "fr": "ENTRÉE* + PLAT DU JOUR + DESSERT*",
                },                  
                "formulaPrice" : "24.90 €",                        
            },
            {
                "entryType": "endingNote",
                "text": 
                  {
                  "fr" : "*Les entrées et desserts comprenant un astérisque ne font pas partis du Menu du Jour",
                  },
                "centering": "center",
            },
            {
                "entryType": "formula",
                "entriesTitle": {
                    "fr": "",
                },
                "entries": [
                    {
                        "entryId": 0,
                        "entryTitle": {
                            "fr": "",                            
                        },                                
                    },
                ],
            },
        ]
    },
    {
        "pageType": {"fr": "Menu enfant"},
        "pageEntries": [  
            {
                "entryType": "startingNote",
                "text": {
                    "fr": "Enfants de moins de 12 ans",
                },
                "centering": "center",                
            },                
            {
                "entryType": "formula",
                "entriesTitle": {
                    "fr": "Menu enfant",                    
                },
                "startingNote": "starting note",                        
                "entries": [
                    {
                        "entryId": 0,
                        "entryTitle": {
                            "fr": "Entrée 1",                            
                        },
                    },
                    {
                        "entryId": 1,
                        "entryTitle": {
                            "fr": "Entrée 2",                            
                        },
                    },
                    {
                        "entryId": 2,
                        "separator": true,
                    },
                    {
                        "entryId": 3,
                        "entryTitle": {
                            "fr": "Plat 1",                            
                        },
                    }, 
                    {
                        "entryId": 4,
                        "entryTitle": {
                            "fr": "Plat 2",                           
                        },
                    }, 
                    {
                        "entryId": 5,
                        "separator": true,
                    },
                    {
                        "entryId": 6,
                        "entryTitle": {
                            "fr": "Dessert 1",                            
                        },
                    }, 
                    {
                        "entryId": 7,
                        "entryTitle": {
                            "fr": "Dessert 2",                           
                        },
                    },                                 
                ],                    
            },                                           
        ]
    },
    {
      "pageType": {
        "fr": "Entrées",
      },
      "pageEntries": [
        {
          "entryType": "startingNote",
          "text": {
            "fr": "Tous nos plats sont préparés avec des produits frais et locaux",
          }
        },
        {
          "entryType": "dishList",
          "entriesTitle": {
            "fr": "",
          },
          "entries": [
            {
              "entryId": 0,
              "entryTitle": {
                "fr": "Soupe à l'oignon gratinée",
              },
              "entrySubtitle": {
                "fr": "Oignons caramélisés, bouillon de bœuf, fromage gratiné",
              },
              "entryPrice": {
                "fr": ["12€"],
              }
            },
            {
              "entryId": 1,
              "entryTitle": {
                "fr": "Salade César",
              },
              "entrySubtitle": {
                "fr": "Poulet grillé, croûtons, parmesan, sauce César",
              },
              "entryPrice": {
                "fr": ["14€"],
              }
            }
          ]
        },        
      ]
    },
    {
      "pageType": {
        "fr": "Plats Principaux",
      },
      "pageEntries": [
        {
          "entryType": "dishList",
          "entriesTitle": {
            "fr": "",
          },
          "entries": [
            {
              "entryId": 0,
              "entryTitle": {
                "fr": "Entrecôte Grillée",
              },
              "entrySubtitle": {
                "fr": "300g, pommes de terre sautées, sauce au poivre",
              },
              "entryPrice": {
                "fr": ["28€"],
              }
            },
            {
              "entryId": 1,
              "entryTitle": {
                "fr": "Poulet Rôti",
              },
              "entrySubtitle": {
                "fr": "Accompagné de légumes de saison",
              },
              "entryPrice": {
                "fr": ["22€"],
              }
            }
          ]
        },        
      ]
    },
    {
      "pageType": {
        "fr": "Pizzas",
      },
      "pageEntries": [
        {
          "entryType": "startingNote",
          "text": {
            "fr": "Nous pétrissons notre propre pâte à pizza chaque jour",
          }
        },
        {
          "entryType": "dishList",
          "entriesTitle": {
            "fr": "",
          },
          "entries": [
            {
              "entryId": 0,
              "entryTitle": {
                "fr": "Pizza Napolitaine",
              },
              "entrySubtitle": {
                "fr": "Tomate, mozzarella, basilic frais",
              },
              "entryPrice": {
                "fr": ["12€"],
              }
            },
            {
              "entryId": 1,
              "entryTitle": {
                "fr": "Pizza Reine",
              },
              "entrySubtitle": {
                "fr": "Jambon, champignons, mozzarella",
              },
              "entryPrice": {
                "fr": ["14€"],
              }
            }
          ]
        },        
      ]
    },
    {
      "pageType": {
        "fr": "Desserts",
      },
      "pageEntries": [
        {
          "entryType": "dishList",
          "entries": [
            {
              "entryId": 0,
              "entryTitle": {
                "fr": "Crème Brûlée",
              },
              "entrySubtitle": {
                "fr": "Vanille de Madagascar",
              },
              "entryPrice": {
                "fr": ["8€"],
              }
            },
            {
              "entryId": 1,
              "entryTitle": {
                "fr": "Tarte Tatin",
              },
              "entrySubtitle": {
                "fr": "Pommes caramélisées, pâte feuilletée",
              },
              "entryPrice": {
                "fr": ["7€"],
              }
            }
          ]
        },
      ]
    },
    {
      "pageType": {
        "fr": "Boissons",
      },
      "pageEntries": [
        {
          "entryType": "dishList",
          "entriesTitle": {
            "fr": "Eaux, Softs et Jus",
          },
          "entries": [
            {
              "entryId": 0,
              "entryTitle": {
                "fr": "Coca-Cola",
              },
              "entrySubtitle": {
                "fr": "",
              },
              "entryPrice": {
                "fr": ["3€"],
              }
            },
            {
              "entryId": 1,
              "entryTitle": {
                "fr": "Eau Minérale",
              },
              "entrySubtitle": {
                "fr": "",
              },
              "entryPrice": {
                "fr": ["2€"],
              }
            }
          ]
        },
        {
          "entryType": "dishList",
          "entriesTitle": {
            "fr": "Apéritifs",
          },
          "entries": [
            {
              "entryId": 0,
              "entryTitle": {
                "fr": "Pastis",
              },
              "entrySubtitle": {
                "fr": "",
              },
              "entryPrice": {
                "fr": ["5€"],
              }
            },
            {
              "entryId": 1,
              "entryTitle": {
                "fr": "Kir",
              },
              "entrySubtitle": {
                "fr": "",
              },
              "entryPrice": {
                "fr": ["2€"],
              }
            }
          ]
        }
      ]
    },
    {
      "pageType": {
        "fr": "Note",
      },
      "pageEntries": [
        {
          "entryType": "note",
          "centering": "start",
          "text": {
            "fr": "Prix en euros, taxes et service compris.\n\nChèques non acceptés.\n\nToutes nos viandes proviennent de l'Union européenne",
          }
        },
      ]
    },
  ],
  "styleSettings": {
    "backgroundColor": "#FFFFFF",
    "priceColor": "#000000",
    "textColor": "#000000",
    "textFont": {
      "name": "Arial",
      "location": ""
    },
    "titleFont": {
      "name": "Verdana",
      "location": ""
    }
  }
}
*/
